import React from "react";
import { Link } from "react-router-dom";

/**
 * Public Privacy Policy page.
 *
 * Hosted at /privacy so it can be linked from the App Store Connect listing,
 * the iOS app, and the marketing site. Apple requires this URL on every
 * iOS submission. No authentication required.
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/login"
            className="inline-flex items-center gap-3"
            data-testid="privacy-logo-link"
          >
            <img
              src="/byrd-logo.png"
              alt="My Rep Agency"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/login"
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-slate-900 font-mono-data"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
          Privacy
        </div>
        <h1 className="mt-1 text-4xl font-display font-bold tracking-tight text-slate-900">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-muted-foreground font-mono-data">
          Effective date: February 1, 2026
        </p>

        <div className="prose prose-slate mt-10 text-slate-800 leading-relaxed [&_h2]:font-display [&_h2]:tracking-tight [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-slate-900 [&_h3]:font-display [&_h3]:font-semibold [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:my-3 [&_ul]:my-3 [&_ul]:pl-6 [&_ul]:list-disc [&_li]:my-1 [&_a]:underline [&_a]:text-amber-700 hover:[&_a]:text-amber-800">
          <p>
            This Privacy Policy explains how <strong>Byrd Rep Agency, LLC</strong>{" "}
            ("Byrd," "we," "us," or "our") collects, uses, and protects information
            in connection with the My Rep Agency mobile and web application (the
            "Service"). The Service is a business‑to‑business tool used by lighting
            and electrical manufacturers' representative agencies, their employees,
            and the distributors they authorize.
          </p>

          <h2>1. Who this policy applies to</h2>
          <p>
            This policy applies to anyone who creates or uses an account in the
            Service, including agency administrators, agency staff, and authorized
            distributor users. The Service is not intended for the general public
            or for individuals under the age of 13, and we do not knowingly collect
            information from children.
          </p>

          <h2>2. Information we collect</h2>
          <h3>Account information you provide</h3>
          <ul>
            <li>
              <strong>Identity:</strong> your name, email address, business phone
              number, role within the platform, and the agency you belong to.
            </li>
            <li>
              <strong>Distributor profile (distributor users only):</strong> the
              distributor company name and the city or branch you are associated
              with.
            </li>
            <li>
              <strong>Credentials:</strong> a salted hash of your password. We
              never store your password in plain text.
            </li>
          </ul>

          <h3>Business content you create</h3>
          <ul>
            <li>
              Manufacturer, product, and specification data entered by agency
              administrators.
            </li>
            <li>Search queries and chat messages you submit to the guided product finder.</li>
            <li>Quote and access requests you submit to your rep agency.</li>
          </ul>

          <h3>Information collected automatically</h3>
          <ul>
            <li>
              <strong>Authentication tokens</strong> used to keep you signed in on
              your device.
            </li>
            <li>
              <strong>Technical logs</strong> such as your IP address, device type,
              browser or operating system version, and timestamps of requests.
              These are used to operate the Service, prevent abuse, and diagnose
              errors.
            </li>
          </ul>

          <h3>Face ID / Touch ID (iOS app only)</h3>
          <p>
            If you enable Face ID or Touch ID sign‑in in the iOS app, your
            credentials are stored encrypted in the iOS Keychain on your device
            using Apple's local authentication framework. Your biometric data
            (your face or fingerprint) is processed entirely by your device's
            Secure Enclave and is <strong>never transmitted to My Rep Agency</strong> or
            stored on our servers. You can disable biometric sign‑in at any time
            from the Profile screen in the app.
          </p>

          <h3>Push notifications (iOS and Android apps)</h3>
          <p>
            If you grant the app permission to send push notifications, your
            device generates a unique <strong>push notification token</strong> that
            we store on our servers and associate with your account. The token
            is used only to deliver service announcements from your rep agency
            (for example, new manufacturer rebates, promo flyers, and product
            updates) through Apple Push Notification service (APNs) and Google
            Firebase Cloud Messaging (FCM). The token contains no personal
            information and is automatically deleted from our servers when you
            log out, uninstall the app, or revoke notification permission in
            your device settings. We never use push notifications for marketing
            unrelated to your rep agency relationship.
          </p>

          <h2>3. How we use information</h2>
          <p>We use the information described above to:</p>
          <ul>
            <li>Provide, maintain, and improve the Service;</li>
            <li>
              Authenticate users and route them to the correct rep agency and
              manufacturer catalog;
            </li>
            <li>
              Respond to your product searches and connect you with the right
              pricing contacts at your rep agency;
            </li>
            <li>
              Send transactional communications such as account approvals,
              password resets, and important changes to the Service;
            </li>
            <li>
              Detect, prevent, and respond to fraud, abuse, and security
              incidents; and
            </li>
            <li>Comply with our legal obligations.</li>
          </ul>
          <p>
            We do <strong>not</strong> sell personal information, and we do not
            use your personal information for advertising or behavioral marketing.
          </p>

          <h2>4. How we share information</h2>
          <p>
            We share information only as needed to operate the Service or when
            required by law:
          </p>
          <ul>
            <li>
              <strong>Your rep agency:</strong> If you are a distributor user, the
              rep agency that approved your account can see your name, contact
              information, distributor name and location, and the searches you
              submit so they can serve you.
            </li>
            <li>
              <strong>Service providers acting on our behalf,</strong> including:
              <ul>
                <li>
                  <strong>Cloud hosting</strong> for the Service infrastructure and
                  database;
                </li>
                <li>
                  <strong>Google (Gemini API)</strong> to power the AI‑guided product
                  finder. Chat messages and the manufacturer catalog data sent to
                  generate a result are processed by Google in accordance with{" "}
                  <a
                    href="https://ai.google.dev/gemini-api/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google's API terms
                  </a>
                  . We do not send your password, phone number, or distributor
                  location to Google.
                </li>
              </ul>
            </li>
            <li>
              <strong>Apple</strong> when you install the iOS app, in accordance
              with{" "}
              <a
                href="https://www.apple.com/legal/privacy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apple's privacy policy
              </a>
              .
            </li>
            <li>
              <strong>Legal and safety</strong> disclosures when we believe in good
              faith that disclosure is required by law, subpoena, or to protect the
              rights, property, or safety of Byrd, our users, or the public.
            </li>
            <li>
              <strong>Business transfers:</strong> if Byrd is involved in a merger,
              acquisition, or sale of assets, your information may be transferred
              as part of that transaction.
            </li>
          </ul>

          <h2>5. Data retention</h2>
          <p>
            We retain account information for as long as your account is active.
            When an account is deleted, identifying information is removed within
            30 days, except where we are required to retain certain records for
            legal, accounting, or fraud‑prevention purposes. Aggregated, de‑identified
            data may be retained indefinitely.
          </p>

          <h2>6. Your choices and rights</h2>
          <ul>
            <li>
              <strong>Access and correction:</strong> you can view and update your
              name and password at any time from the Profile screen.
            </li>
            <li>
              <strong>Deletion:</strong> contact us at the address below to request
              deletion of your account. If you are a distributor user, your rep
              agency administrator can also remove your access.
            </li>
            <li>
              <strong>Biometric sign‑in:</strong> can be enabled or disabled at any
              time from the Profile screen of the iOS app.
            </li>
            <li>
              <strong>State and regional rights:</strong> depending on where you
              live (for example, California, Virginia, Colorado, the EU/UK), you
              may have additional rights regarding access, portability, correction,
              and deletion. To exercise these rights, contact us using the details
              below.
            </li>
          </ul>

          <h2>7. Security</h2>
          <p>
            We use industry‑standard safeguards to protect your information,
            including TLS in transit, encrypted password hashing (bcrypt), and
            role‑based access controls on the server side. No system is perfectly
            secure, and we cannot guarantee absolute security; please use a strong
            password and notify us immediately if you suspect unauthorized access
            to your account.
          </p>

          <h2>8. International users</h2>
          <p>
            The Service is operated from the United States. If you access the
            Service from outside the United States, you understand that your
            information will be processed in the United States, which may have
            different data protection laws than your country of residence.
          </p>

          <h2>9. Children</h2>
          <p>
            The Service is not directed to children under 13 and we do not
            knowingly collect personal information from children. If you believe
            we have inadvertently collected such information, please contact us
            and we will delete it.
          </p>

          <h2>10. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will revise the "Effective date" above and, where appropriate, notify
            you through the Service. Continued use of the Service after an update
            means you accept the revised policy.
          </p>

          <h2>11. Contact us</h2>
          <p>
            Questions, concerns, or requests under this policy can be sent to:
          </p>
          <p className="font-mono-data text-sm">
            <strong>Byrd Rep Agency, LLC</strong>
            <br />
            Attn: Privacy
            <br />
            <a href="mailto:wayne@myrepagency.com">wayne@myrepagency.com</a>
            <br />
            <a
              href="https://myrepagency.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              myrepagency.com
            </a>
          </p>
        </div>

        <div className="mt-16 pt-6 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono-data">
          <span>© {new Date().getFullYear()} Byrd Rep Agency, LLC</span>
          <div className="flex items-center gap-4">
            <Link to="/support" className="hover:text-slate-900">
              Support
            </Link>
            <Link to="/login" className="hover:text-slate-900">
              Back to sign in →
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
